import { convertNumber, GeckoMeasurement, GeckoMeasurementUnit } from 'types'

export const WORKING_UNIT_THICKNESS = GeckoMeasurementUnit.Millimeters
export const DEFAULT_THICKNESS_UNIT = GeckoMeasurementUnit.Millimeters
export const WORKING_UNIT_PRESSURE = GeckoMeasurementUnit.Megapascals

/**
 * Decimal places for numeric values displayed in the FFS workflow (thickness,
 * stress, pressure, flaw sizes, etc.). SME-mandated: FFS shows 3 places where
 * the rest of the app defaults to 2. Route display through `formatFfs` /
 * `formatFfsScalar` below so this precision lives in ONE place. Does NOT govern
 * integer counts or plot tick spacing, which stay at their own precision.
 */
export const FFS_DISPLAY_DECIMALS = 3

/**
 * Convert a raw scalar between units (canonical numeric conversion). Thin alias
 * over the shared `convertNumber` so FFS code has a single conversion entry point
 * and never hand-rolls `GeckoMeasurement.CreateFromValue(...).convertTo(...)`.
 */
export const convertFfs = (
  value: number,
  from: GeckoMeasurementUnit,
  to: GeckoMeasurementUnit
): number => convertNumber(value, from, to)

/** Format a measurement in `unit` at the FFS display precision. */
export const formatFfs = (
  measurement: GeckoMeasurement,
  unit: GeckoMeasurementUnit
): string => measurement.convertTo(unit).num.toFixed(FFS_DISPLAY_DECIMALS)

/** Format a raw scalar (given in `from`) shown in `to` at the FFS precision. */
export const formatFfsScalar = (
  value: number,
  from: GeckoMeasurementUnit,
  to: GeckoMeasurementUnit
): string => convertNumber(value, from, to).toFixed(FFS_DISPLAY_DECIMALS)

/**
 * Format a "≥ threshold" comparison string at FFS display precision. Single
 * source for the trailing-text used by the live Part 4/5 criteria sections and
 * the read-only evaluation snapshot.
 * - `opts.unit`: convert the threshold to this display unit (defaults to the
 *   threshold's own stored unit).
 * - `opts.withUnitSuffix`: append " <unit.name>" for dimensioned values.
 * - `opts.fallback`: returned when `threshold` is null/undefined.
 */
export function formatGteThreshold(
  threshold: GeckoMeasurement | null | undefined,
  opts?: {
    unit?: GeckoMeasurementUnit
    withUnitSuffix?: boolean
    fallback?: string
  }
): string | undefined {
  if (!threshold) return opts?.fallback
  const displayUnit = opts?.unit ?? threshold.unit
  const dimensionless =
    displayUnit === GeckoMeasurementUnit.Ratio ||
    displayUnit === GeckoMeasurementUnit.None
  const suffix =
    opts?.withUnitSuffix && !dimensionless ? ` ${displayUnit.name}` : ''
  return `≥ ${formatFfs(threshold, displayUnit)}${suffix}`
}

/** Narrow a plain-number asset-model field (E, Y) to a finite value or null. */
export const finiteOrNull = (n: number | undefined): number | null =>
  typeof n === 'number' && Number.isFinite(n) ? n : null

/** Convert a `GeckoMeasurement` to a plain number in `unit`, or null if absent. */
export const measToNumber = (
  meas: GeckoMeasurement | undefined,
  unit: GeckoMeasurementUnit
): number | null => (meas ? meas.convertTo(unit).num : null)

/** Wrap a plain number in a `GeckoMeasurement` of `unit`, passing null through. */
export const toMeas = (
  num: number | null,
  unit: GeckoMeasurementUnit
): GeckoMeasurement | null =>
  num === null ? null : GeckoMeasurement.CreateFromValue(num, unit)
